import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { theme } from '@styles/theme';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  position?: 'left' | 'right';
  children: React.ReactNode;
}

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${theme.colors.surface.overlay};
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  z-index: ${theme.zIndex.modal};
  backdrop-filter: blur(2px);
  animation: fadeIn ${theme.motion.duration.fast} ${theme.motion.easing.standard};

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const DrawerContainer = styled.div<{ isOpen: boolean; position: 'left' | 'right' }>`
  position: fixed;
  top: 0;
  ${(props) => (props.position === 'left' ? 'left: 0' : 'right: 0')};
  width: 280px;
  height: 100vh;
  background-color: ${theme.colors.surface.base};
  box-shadow: ${theme.shadows.xl};
  border-left: ${(props) => (props.position === 'right' ? `1px solid ${theme.colors.border.light}` : 'none')};
  border-right: ${(props) => (props.position === 'left' ? `1px solid ${theme.colors.border.light}` : 'none')};
  z-index: ${theme.zIndex.modal + 1};
  overflow-y: auto;
  transform: ${(props) =>
    props.isOpen
      ? 'translateX(0)'
      : props.position === 'left'
      ? 'translateX(-100%)'
      : 'translateX(100%)'};
  transition: transform ${theme.motion.duration.normal} ${theme.motion.easing.standard};

  @media (min-width: ${theme.breakpoints.tablet}) {
    width: 320px;
  }
`;

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  position = 'left',
  children,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const drawerContent = (
    <Overlay isOpen={isOpen} onClick={onClose}>
      <DrawerContainer
        isOpen={isOpen}
        position={position}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </DrawerContainer>
    </Overlay>
  );

  return ReactDOM.createPortal(drawerContent, document.body);
};

